import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Clock, FileText, X, ArrowRight, Check, AlertTriangle, Users, RefreshCw, UserPlus, Building2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { API } from '../lib/axiosClient';

const TYPE_CONFIG = {
  delegation: { icon: Users, color: 'bg-purple-100 text-purple-600', label: 'Delegation' },
  assignment: { icon: ArrowRight, color: 'bg-blue-100 text-blue-600', label: 'Zuweisung' },
  escalation: { icon: AlertTriangle, color: 'bg-red-100 text-red-600', label: 'Eskalation' },
  status_change: { icon: RefreshCw, color: 'bg-green-100 text-green-600', label: 'Status' },
  action_assigned: { icon: Check, color: 'bg-amber-100 text-amber-600', label: 'Maßnahme' },
  rework_required: { icon: AlertTriangle, color: 'bg-orange-100 text-orange-600', label: 'Nacharbeit' },
  reminder: { icon: Clock, color: 'bg-amber-100 text-amber-600', label: 'Erinnerung' },
  // Hybrid-Identity (Admin): SAP-Einladungen/Registrierungen/Governance
  sap_user_invited: { icon: UserPlus, color: 'bg-indigo-100 text-indigo-600', label: 'Einladung' },
  sap_invite_resent: { icon: UserPlus, color: 'bg-indigo-100 text-indigo-600', label: 'Einladung' },
  sap_invite_revoked: { icon: X, color: 'bg-slate-100 text-slate-500', label: 'Einladung' },
  sap_invite_expired: { icon: Clock, color: 'bg-amber-100 text-amber-600', label: 'Einladung' },
  sap_invite_failed: { icon: AlertTriangle, color: 'bg-red-100 text-red-600', label: 'Einladung' },
  sap_registration_completed: { icon: ShieldCheck, color: 'bg-emerald-100 text-emerald-600', label: 'Registrierung' },
  sap_registration_pending_approval: { icon: ShieldAlert, color: 'bg-amber-100 text-amber-600', label: 'Freigabe' },
  sap_registration_approved: { icon: ShieldCheck, color: 'bg-emerald-100 text-emerald-600', label: 'Freigabe' },
  sap_registration_rejected: { icon: X, color: 'bg-red-100 text-red-600', label: 'Freigabe' },
  sap_owner_without_login: { icon: Building2, color: 'bg-amber-100 text-amber-600', label: 'SAP-Owner' },
  entra_role_mapping_missing: { icon: ShieldAlert, color: 'bg-orange-100 text-orange-600', label: 'Entra' },
  entra_default_role_used: { icon: ShieldAlert, color: 'bg-orange-100 text-orange-600', label: 'Entra' },
  identity_duplicate_match_warning: { icon: AlertTriangle, color: 'bg-orange-100 text-orange-600', label: 'Identität' },
};

export const NotificationBell = () => {
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [inAppNotifications, setInAppNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const [reminders, inApp] = await Promise.all([
        axios.get(`${API}/notes/reminders/due`, { timeout: 5000 }).catch(() => ({ data: [] })),
        axios.get(`${API}/notifications/in-app`, { timeout: 5000 }).catch(() => ({ data: [] })),
      ]);
      setNotifications(reminders.data || []);
      setInAppNotifications(inApp.data || []);
    } catch {
      // Silent
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const count = notifications.length + inAppNotifications.length;

  const handleNotificationClick = async (item, isInApp) => {
    if (isInApp && item.id) {
      try {
        await axios.put(`${API}/notifications/in-app/${item.id}/read`, {});
        setInAppNotifications(prev => prev.filter(n => n.id !== item.id));
      } catch { /* silent */ }
    }
    // Hybrid-Identity & andere Admin-Events liefern eine action_url (Deep-Link).
    if (item.action_url) {
      navigate(item.action_url);
      setIsOpen(false);
      return;
    }
    // QM8D Rev.1000 Bug-Fix: complaint_number ist stabiler als die UUID
    // (Stale-Notifications zeigen oft auf alte UUIDs, die nicht mehr existieren).
    const target = item.complaint_number || item.complaint_id;
    if (target) navigate(`/complaints/${target}/edit`);
    setIsOpen(false);
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/in-app/read-all`, {});
      setInAppNotifications([]);
    } catch { /* silent */ }
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
        data-testid="notification-bell-btn"
        title={t("nav.notifications")}
      >
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full animate-pulse" data-testid="notification-badge">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 sm:right-0 top-full mt-2 w-[calc(100vw-24px)] sm:w-96 max-w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-800">{t('nav.notificationsTitle')}</h3>
            <div className="flex items-center gap-2">
              {inAppNotifications.length > 0 && (
                <button onClick={handleMarkAllRead} className="text-xs text-blue-500 hover:text-blue-700" data-testid="mark-all-read-btn">
                  Alle gelesen
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {count === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="no-notifications">{t('nav.noNotifications')}</div>
            ) : (
              <>
                {inAppNotifications.map((n, idx) => {
                  const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.reminder;
                  const Icon = cfg.icon;
                  const sevBorder = { warning: 'border-l-2 border-l-amber-400', error: 'border-l-2 border-l-red-400', success: 'border-l-2 border-l-emerald-400' }[n.severity] || '';
                  return (
                    <button
                      key={n.id || `inapp-${idx}`}
                      onClick={() => handleNotificationClick(n, true)}
                      className={`w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors ${sevBorder}`}
                      data-testid={`notification-inapp-${idx}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full ${cfg.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{n.title}</p>
                          <p className="text-xs text-slate-500 truncate">{n.body}</p>
                          {n.complaint_number && (
                            <div className="flex items-center gap-1 mt-0.5 text-xs text-slate-400">
                              <FileText className="w-3 h-3" />
                              <span>{n.complaint_number}</span>
                            </div>
                          )}
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            {n.created_at && new Date(n.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
                {notifications.map((note, idx) => (
                  <button
                    key={note.id || `rem-${idx}`}
                    onClick={() => handleNotificationClick(note, false)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors"
                    data-testid={`notification-reminder-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Clock className="w-4 h-4 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{note.text}</p>
                        {note.complaint_number && (
                          <div className="flex items-center gap-1 mt-0.5 text-xs text-slate-400">
                            <FileText className="w-3 h-3" />
                            <span>{note.complaint_number}</span>
                          </div>
                        )}
                        {note.reminder_date && (
                          <p className="text-[10px] text-amber-600 mt-0.5">
                            {new Date(note.reminder_date).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
